import { useCallback, useEffect, useState } from 'react';
import {
  Users,
  RefreshCw,
  AlertTriangle,
  Infinity as InfinityIcon,
  ShieldCheck,
  ShieldOff,
  X,
} from 'lucide-react';
import { adminApi, AdminCustomer } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { LLM_MODELS } from '../../types';

interface CustomerDetail {
  user: AdminCustomer;
  days: number;
  lifetime: { generations: number; failed_events: number; last_active: string | null };
  window: {
    generations: number;
    transcriptions: number;
    avg_latency_ms: number;
    cost_usd: number;
    api_calls: number;
    top_exam_categories: { category: string; count: number }[];
  };
  open_feedback: number;
}

interface ErrorData {
  counts_by_type: { signal_type: string; count: number }[];
  signals: {
    id: string;
    signal_type: string;
    severity: string;
    exam_category: string | null;
    created_at: string;
  }[];
}

interface ActivityRow {
  id: string;
  event_type: string;
  exam_category: string | null;
  model_used: string | null;
  status: string;
  error_message: string | null;
  latency_ms: number | null;
  created_at: string;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const fmtCost = (usd: number) => `$${usd.toFixed(2)}`;

export default function Customers({ days }: { days: number }) {
  const { user: me } = useAuthStore();
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [errors, setErrors] = useState<ErrorData | null>(null);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [creditsInput, setCreditsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.customers(days);
      setCustomers(data.customers);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCustomer = useCallback(
    async (id: string) => {
      setSelectedId(id);
      setSaveError(null);
      const [d, e, a] = await Promise.all([
        adminApi.customerDetail(id, days),
        adminApi.customerErrors(id, days),
        adminApi.customerActivity(id, { limit: 25, errors_only: errorsOnly }),
      ]);
      setDetail(d);
      setErrors(e);
      setActivity(a);
      setCreditsInput(d.user.free_reports_remaining === null ? '' : String(d.user.free_reports_remaining));
    },
    [days, errorsOnly]
  );

  useEffect(() => {
    if (selectedId) {
      adminApi.customerActivity(selectedId, { limit: 25, errors_only: errorsOnly }).then(setActivity);
    }
  }, [errorsOnly, selectedId]);

  const applyUpdate = async (payload: Parameters<typeof adminApi.updateCustomer>[1]) => {
    if (!selectedId) return;
    setSaving(true);
    setSaveError(null);
    try {
      await adminApi.updateCustomer(selectedId, payload);
      await Promise.all([openCustomer(selectedId), fetchList()]);
    } catch (err: unknown) {
      const detailMsg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setSaveError(detailMsg || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const roleBadge = (role: string) =>
    role === 'admin' ? (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
        admin
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-surface-800 dark:text-gray-300">
        radiologist
      </span>
    );

  const creditsCell = (c: AdminCustomer) =>
    c.free_reports_remaining === null ? (
      <span className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400">
        <InfinityIcon size={14} /> unlimited
      </span>
    ) : (
      <span className={c.free_reports_remaining === 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
        {c.free_reports_remaining}
      </span>
    );

  return (
    <div className="space-y-4">
      {/* Customer list */}
      <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-primary-600" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              Customers <span className="text-gray-400 font-normal">({customers.length})</span>
            </h2>
          </div>
          <button
            onClick={fetchList}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-surface-800">
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Credits</th>
                <th className="px-3 py-2 font-medium text-right">Reports ({days}d)</th>
                <th className="px-3 py-2 font-medium text-right">Errors</th>
                <th className="px-3 py-2 font-medium text-right">Cost ({days}d)</th>
                <th className="px-4 py-2 font-medium">Last active</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => openCustomer(c.id)}
                  className={`cursor-pointer border-b border-gray-100 dark:border-surface-800/60 hover:bg-gray-50 dark:hover:bg-surface-800/50 ${
                    selectedId === c.id ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''
                  }`}
                >
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900 dark:text-white">{c.full_name}</div>
                    <div className="text-xs text-gray-500">{c.email}</div>
                  </td>
                  <td className="px-3 py-2.5">{roleBadge(c.role)}</td>
                  <td className="px-3 py-2.5">
                    {c.is_active ? (
                      <span className="text-green-600 dark:text-green-400 text-xs font-medium">active</span>
                    ) : (
                      <span className="text-gray-400 text-xs font-medium">deactivated</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">{creditsCell(c)}</td>
                  <td className="px-3 py-2.5 text-right">{c.generations}</td>
                  <td className="px-3 py-2.5 text-right">
                    {c.failed_events + c.error_signals > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle size={13} />
                        {c.failed_events + c.error_signals}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">{fmtCost(c.cost_usd)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(c.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drill-in */}
      {detail && (
        <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-surface-800">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {detail.user.full_name}
                <span className="ml-2 text-xs font-normal text-gray-500">{detail.user.email}</span>
              </h3>
            </div>
            <button
              onClick={() => {
                setSelectedId(null);
                setDetail(null);
              }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md"
            >
              <X size={14} />
            </button>
          </div>

          <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Account controls */}
            <div className="space-y-4">
              <div className="rounded-md border border-gray-200 dark:border-surface-800 p-3 space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Account</h4>
                {saveError && (
                  <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1.5">{saveError}</div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-16">Credits</label>
                  <input
                    type="number"
                    min={0}
                    value={creditsInput}
                    placeholder="∞"
                    onChange={(e) => setCreditsInput(e.target.value)}
                    className="w-24 text-sm border border-gray-300 dark:border-surface-700 rounded px-2 py-1 bg-white dark:bg-surface-800"
                  />
                  <button
                    disabled={saving || creditsInput === ''}
                    onClick={() => applyUpdate({ free_reports_remaining: Number(creditsInput) })}
                    className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded disabled:opacity-40"
                  >
                    Set
                  </button>
                  <button
                    disabled={saving}
                    onClick={() => applyUpdate({ free_reports_remaining: null })}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-surface-700 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-800"
                    title="Unlimited reports"
                  >
                    <InfinityIcon size={12} className="inline" /> Unlimited
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-16">AI model</label>
                  <select
                    value={detail.user.llm_model ?? ''}
                    disabled={saving}
                    onChange={(e) => applyUpdate({ llm_model: e.target.value || null })}
                    className="text-sm border border-gray-300 dark:border-surface-700 rounded px-2 py-1 bg-white dark:bg-surface-800 disabled:opacity-50"
                    title="Report-writing model for this account. Radiologists don't see or choose this."
                  >
                    <option value="">System default</option>
                    {LLM_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-16">Role</label>
                  <select
                    value={detail.user.role}
                    disabled={saving || detail.user.id === me?.id}
                    onChange={(e) => applyUpdate({ role: e.target.value })}
                    className="text-sm border border-gray-300 dark:border-surface-700 rounded px-2 py-1 bg-white dark:bg-surface-800 disabled:opacity-50"
                  >
                    <option value="radiologist">radiologist</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-16">Status</label>
                  {detail.user.is_active ? (
                    <button
                      disabled={saving || detail.user.id === me?.id}
                      onClick={() => applyUpdate({ is_active: false })}
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                    >
                      <ShieldOff size={12} /> Deactivate
                    </button>
                  ) : (
                    <button
                      disabled={saving}
                      onClick={() => applyUpdate({ is_active: true })}
                      className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-green-300 dark:border-green-800 text-green-600 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                    >
                      <ShieldCheck size={12} /> Reactivate
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-gray-400">
                  Joined {fmtDate(detail.user.created_at)} · {detail.lifetime.generations} reports lifetime ·{' '}
                  {detail.open_feedback} open feedback
                </p>
              </div>

              {/* Window stats */}
              <div className="rounded-md border border-gray-200 dark:border-surface-800 p-3">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Last {detail.days} days
                </h4>
                <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
                  <dt className="text-gray-500 text-xs">Reports</dt>
                  <dd className="text-right font-medium">{detail.window.generations}</dd>
                  <dt className="text-gray-500 text-xs">Transcriptions</dt>
                  <dd className="text-right font-medium">{detail.window.transcriptions}</dd>
                  <dt className="text-gray-500 text-xs">API cost</dt>
                  <dd className="text-right font-medium">{fmtCost(detail.window.cost_usd)}</dd>
                  <dt className="text-gray-500 text-xs">API calls</dt>
                  <dd className="text-right font-medium">{detail.window.api_calls}</dd>
                  <dt className="text-gray-500 text-xs">Avg latency</dt>
                  <dd className="text-right font-medium">
                    {(detail.window.avg_latency_ms / 1000).toFixed(1)}s
                  </dd>
                </dl>
                {detail.window.top_exam_categories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {detail.window.top_exam_categories.map((c) => (
                      <span
                        key={c.category}
                        className="px-1.5 py-0.5 text-[11px] rounded bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300"
                      >
                        {c.category} × {c.count}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Error signals */}
            <div className="rounded-md border border-gray-200 dark:border-surface-800 p-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Error signals ({detail.days}d)
              </h4>
              {errors && errors.counts_by_type.length > 0 ? (
                <>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {errors.counts_by_type.map((c) => (
                      <span
                        key={c.signal_type}
                        className="px-1.5 py-0.5 text-[11px] rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
                      >
                        {c.signal_type} × {c.count}
                      </span>
                    ))}
                  </div>
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                    {errors.signals.map((s) => (
                      <li key={s.id} className="text-xs text-gray-600 dark:text-gray-300">
                        <span
                          className={`font-medium ${
                            s.severity === 'warning' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'
                          }`}
                        >
                          {s.signal_type}
                        </span>
                        {s.exam_category && <span className="text-gray-400"> · {s.exam_category}</span>}
                        <span className="text-gray-400"> · {fmtDate(s.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <p className="text-xs text-gray-400">No error signals in this window.</p>
              )}
            </div>

            {/* Recent activity */}
            <div className="rounded-md border border-gray-200 dark:border-surface-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recent activity</h4>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={errorsOnly}
                    onChange={(e) => setErrorsOnly(e.target.checked)}
                    className="rounded"
                  />
                  errors only
                </label>
              </div>
              <ul className="space-y-1.5 max-h-80 overflow-y-auto">
                {activity.length === 0 && <li className="text-xs text-gray-400">No events.</li>}
                {activity.map((a) => (
                  <li key={a.id} className="text-xs">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`w-1.5 h-1.5 rounded-full flex-none ${
                          a.status === 'error' ? 'bg-red-500' : 'bg-green-500'
                        }`}
                      />
                      <span className="font-medium text-gray-700 dark:text-gray-200">{a.event_type}</span>
                      {a.exam_category && <span className="text-gray-400">{a.exam_category}</span>}
                      {a.latency_ms != null && (
                        <span className="text-gray-400">{(a.latency_ms / 1000).toFixed(1)}s</span>
                      )}
                      <span className="text-gray-400 ml-auto">{fmtDate(a.created_at)}</span>
                    </div>
                    {a.error_message && (
                      <div className="ml-3 text-red-600 dark:text-red-400 truncate" title={a.error_message}>
                        {a.error_message}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
